import { Link } from "wouter";
import { api, type PostingIdentity } from "../lib/api";
import { DetailBreadcrumb } from "./DetailBreadcrumb";
import { useI18n } from "../lib/i18n";
import { useState, type ReactNode, type RefObject } from "react";
import { Bookmark, LockKeyhole, MessageCircle, Package } from "lucide-react";
import { LocationPin } from "./LocationPin";
import { MobileActionBar } from "./MobileActionBar";
import { ImageCarousel } from "./ImageCarousel";
import { SaveButton } from "./SaveButton";
import { AccountVerificationBadge } from "./AccountVerificationBadge";
import { isRequesterSummary } from "./RfqRequesterCard";
import { accountVerificationFrom } from "../lib/accountVerification";

import { RfqTypeBadge, type RfqTypeDetail } from "./RfqTypeBadge";
import { RfqDesiredTermsSummary } from "./RfqDesiredTerms";
import type { RfqDesiredMilestone } from "../lib/rfqDesiredTerms";

type RfqLine = { name: string; kind?: string; qty?: number; unit?: string; spec?: string; desiredPrice?: number };
type RfqListing = {
  postingIdentity?: PostingIdentity;
  id: string;
  title: string;
  category: string;
  location: string;
  deadline: string;
  owner: string;
  ownerUserId?: number | null;
  requesterSummary?: unknown;
  quotes?: number;
  status: string;
  items: RfqLine[];
  detail?: RfqTypeDetail & { description?: string; terms?: string; previousWork?: string; desiredMilestones?: RfqDesiredMilestone[] };
};
type SupplierQuote = { total: number; status: string };

type RfqDetailMobileProps = {
  rfq: RfqListing;
  items: RfqLine[];
  signedIn: boolean;
  canQuote: boolean;
  isOwner: boolean;
  awarded: boolean;
  closed: boolean;
  deadlineExpired?: boolean;
  myQuote: SupplierQuote | null;
  quoteError: string | null;
  quotePanel: ReactNode;
  quotesPanel: ReactNode;
  suggestedOrgs: ReactNode;
  requesterCard: ReactNode;
  itemProgressPanel?: ReactNode;
  quoteRef: RefObject<HTMLDivElement>;
  quoteIsVisible: boolean;
  quotationAction: { label: string; available: boolean; disabled: boolean; onClick: () => void };
  historicalQuoteContent?: ReactNode;
  standby: boolean;
  onSignIn: () => void;
  onManageActivities: () => void;
  onMessageBuyer: () => void;
};

export function RfqDetailMobile({
  rfq, items, signedIn, isOwner, awarded, closed, deadlineExpired = false, quoteError, quotesPanel,
  suggestedOrgs, requesterCard, itemProgressPanel, quoteRef, quoteIsVisible, quotationAction, historicalQuoteContent, standby, onSignIn, onMessageBuyer,
}: RfqDetailMobileProps) {
  const { t } = useI18n();
  const [previousWorkExpanded, setPreviousWorkExpanded] = useState(false);
  const [failedBusinessImage, setFailedBusinessImage] = useState<string | null>(null);
  const canMessageBuyer = signedIn && !isOwner && rfq.ownerUserId != null;
  // [rfq.request-type](../../../docs/product/rfqs.md#rfq-request-type): old Services lines remain visible; new Services requests use Description.
  const showLineItems = rfq.detail?.requestType !== "services" || items.length > 0;
  const statusLabel = awarded ? "Awarded" : rfq.status === "completed" ? "Completed" : deadlineExpired ? "Quotation deadline passed" : closed ? "Closed" : "Open for quotes";
  const requester = isRequesterSummary(rfq.requesterSummary) ? rfq.requesterSummary : null;
  const business = rfq.postingIdentity?.kind === "business" ? rfq.postingIdentity : null;
  const requesterName = business?.name || requester?.name || rfq.owner;
  const initials = requesterName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "B";
  const showActions = !isOwner && quotationAction.available;
  // Keep the established sentence-case accessible name across quotation entry points.
  const actionName = quotationAction.label === "Create Quotation" ? "Create quotation" : quotationAction.label;

  return <div data-testid="rfq-detail-mobile" className={`bg-[#f3f6f9] text-navy ${showActions ? "pb-24" : "pb-6"}`}>
    <DetailBreadcrumb title={rfq.title} fallback={{ href: "/marketplace?tab=RFQs", label: "RFQs" }} className="px-4 py-3 text-xs" />

    <div className="relative">
      <ImageCarousel type="rfq" id={rfq.id} compact narrowThumbnails mobileService />
      {!isOwner && (signedIn
        ? <SaveButton kind="rfq" refId={rfq.id} imageOverlay iconClassName="h-6 w-6" className="absolute right-4 top-3 z-20 h-10 w-10" />
        : <button type="button" onClick={onSignIn} aria-label="Sign in to save RFQ" className="absolute right-4 top-3 z-20 grid h-10 w-10 place-items-center text-white"><Bookmark className="h-6 w-6 fill-white" /></button>)}
    </div>

    <section className="bg-white px-4 pb-5 pt-5">
      <h1 className="break-words text-[25px] font-extrabold leading-tight tracking-tight text-navy">{rfq.title}</h1>
      <div className="mt-4 flex min-w-0 items-center gap-2.5">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#dce9f0] text-sm font-extrabold text-[#1e4c6a]">{business?.imageUrl && failedBusinessImage !== business.imageUrl ? <img src={api.apiAssetUrl(business.imageUrl) || undefined} alt="" className="h-full w-full rounded-xl object-cover" onError={() => setFailedBusinessImage(business.imageUrl)} /> : initials}</div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5 text-sm font-extrabold">{business?.href ? <Link href={business.href} className="min-w-0 truncate" title={requesterName}>{requesterName}</Link> : <strong className="min-w-0 truncate" title={requesterName}>{requesterName}</strong>}{!business && <AccountVerificationBadge {...accountVerificationFrom(requester)} />}</div>
          <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><LocationPin className="h-3.5 w-3.5" />{rfq.location}</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        <div className="flex max-w-full shrink-0 flex-wrap items-center gap-1.5 [&>span]:shrink-0 [&>span]:whitespace-nowrap">
          <RfqTypeBadge requestType={rfq.detail?.requestType} />
          <span className={`rounded-full px-2.5 py-1.5 text-[11px] font-bold ${awarded ? "bg-amber-50 text-amber-800" : closed ? "bg-slate-100 text-slate-600" : "bg-[#e5f5ea] text-[#167a4b]"}`}>{statusLabel}</span>
        </div>
        <span className="rounded-full bg-[#ebf2f7] px-2.5 py-1.5 text-[11px] font-bold text-[#305676]">{rfq.category}</span>
        <span className="rounded-full bg-[#ebf2f7] px-2.5 py-1.5 text-[11px] font-bold text-[#305676]">{rfq.location}</span>
      </div>

      {rfq.detail?.description && <p className="mt-4 whitespace-pre-line text-sm leading-6 text-[#49627a]">{rfq.detail.description}</p>}
      <RfqDesiredTermsSummary requestType={rfq.detail?.requestType} milestones={rfq.detail?.desiredMilestones} items={items} />
      {rfq.detail?.previousWork && <div className="mt-3 rounded-xl bg-[#f1f6fa] p-3"><strong className="text-xs">{t("posting.previousWork")}</strong><p className={`mt-1 whitespace-pre-line text-xs leading-5 text-slate-600 ${previousWorkExpanded ? "" : "line-clamp-3"}`}>{rfq.detail.previousWork}</p>{rfq.detail.previousWork.length > 150 && <button type="button" onClick={() => setPreviousWorkExpanded((expanded) => !expanded)} aria-expanded={previousWorkExpanded} className="mt-2 block w-full text-right text-xs font-bold text-blue-700">{previousWorkExpanded ? "Show less" : "View more"}</button>}</div>}

      <div className={`mt-5 grid ${showLineItems ? "grid-cols-3" : "grid-cols-2"} border-t border-slate-200 pt-4 text-center`}>
        {showLineItems && <div><strong className="block text-base">{items.length}</strong><span className="text-[11px] text-slate-500">Requested items</span></div>}
        <div className={`${showLineItems ? "border-x" : "border-r"} border-slate-200`}><strong className="block text-base">{rfq.quotes ?? 0}</strong><span className="text-[11px] text-slate-500">Quotes received</span></div>
        <div><strong className="block text-xs leading-5">{rfq.deadline}</strong><span className="text-[11px] text-slate-500">Quote deadline</span></div>
      </div>

      {showActions && <div ref={quoteRef} className="mt-4 flex gap-2" data-testid="rfq-mobile-primary-actions">
        <button type="button" onClick={quotationAction.onClick} disabled={quotationAction.disabled} aria-label={actionName} className="btn-red h-12 min-w-0 flex-1 rounded-xl text-sm font-bold disabled:opacity-50">{quotationAction.label}</button>
        {canMessageBuyer && <button type="button" onClick={onMessageBuyer} aria-label="Message buyer" className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-slate-300 text-navy"><MessageCircle className="h-5 w-5" /></button>}
      </div>}
      {showActions && <p className="mt-3 flex items-center gap-2 text-xs text-slate-500"><LockKeyhole className="h-4 w-4 shrink-0 text-brand-green" />Your quotation is sent privately to the buyer in Messages.</p>}
      {deadlineExpired && <p className="mt-3 text-sm text-slate-500">Quotation deadline passed.</p>}
      {!isOwner && standby && <p className="mt-3 text-xs text-slate-500">This RFQ has no specified type. Quotation submission is on standby.</p>}
      {quoteError && <p role="alert" className="mt-3 text-xs text-brand-red">{quoteError}</p>}
    </section>

    <div className="mt-2 [&>.card]:px-4 [&>.card]:py-5">{requesterCard}</div>

    {(showLineItems || rfq.detail?.terms) && <section className="mt-2 bg-white px-4 py-5">
      {showLineItems && <>
      <div className="flex items-center gap-2"><Package className="h-5 w-5 text-[#356484]" /><h2 className="text-lg font-extrabold">Requested items <span className="text-slate-500">({items.length})</span></h2></div>
      {items.length > 0 ? <div className="mt-2 divide-y divide-slate-200">{items.map((item, index) => <article key={`${item.name}-${index}`} className="py-3">
        <div className="flex items-start justify-between gap-3"><strong className="min-w-0 text-sm leading-5">{index + 1}. {item.name}</strong><span className="shrink-0 rounded-lg bg-[#edf3f7] px-2.5 py-1 text-[11px] font-bold text-[#355a77]">{item.kind === "lump" ? "Lump sum" : `${item.qty ?? 0} ${item.unit || "units"}`}</span></div>
        {item.spec && <p className="mt-1 text-xs leading-5 text-slate-500">{item.spec}</p>}
      </article>)}</div> : <p className="mt-3 text-sm text-slate-500">No line items were listed.</p>}
      </>}
      {rfq.detail?.terms && <p className="mt-2 border-t border-slate-200 pt-3 text-xs leading-5 text-slate-600"><strong className="text-navy">Terms:</strong> {rfq.detail.terms}</p>}
    </section>}

    {itemProgressPanel && <div className="mt-2 [&>.card]:rounded-none [&>.card]:border-x-0 [&>.card]:px-4 [&>.card]:py-5">{itemProgressPanel}</div>}
    {historicalQuoteContent && <details className="mt-2 bg-white px-4 py-5"><summary className="cursor-pointer text-sm font-bold">Historical quotation</summary>{historicalQuoteContent}</details>}
    {isOwner && quotesPanel && <div className="mt-2 [&>.card]:rounded-none [&>.card]:border-x-0 [&>.card]:px-4 [&>.card]:py-5">{quotesPanel}</div>}

    <div className="mt-2 [&>.card]:rounded-none [&>.card]:border-x-0 [&>.card]:px-4 [&>.card]:py-5">{suggestedOrgs}</div>

    {showActions && !quoteIsVisible && <MobileActionBar>
      <div className="flex w-full gap-2" data-testid="rfq-mobile-sticky-actions">
        <button type="button" onClick={quotationAction.onClick} disabled={quotationAction.disabled} aria-label={actionName} className="btn-red h-11 min-w-0 flex-1 rounded-xl px-3 text-sm font-bold disabled:opacity-50">{quotationAction.label}</button>
        {canMessageBuyer && <button type="button" onClick={onMessageBuyer} aria-label="Message buyer" className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-slate-300 text-navy"><MessageCircle className="h-5 w-5" /></button>}
      </div>
    </MobileActionBar>}
  </div>;
}
